<template>
    <div class="relative">
        <label
            class="relative block h-9"
            :title="`${selectedAccount?.card?.title || ''} ${selectedAccount?.last_four ? `(${selectedAccount?.last_four})` : ''}`"
            v-click-outside="closeDropdown"
        >
            <input
                class="h-9 w-full flex items-center gap-2 text-left pl-4 text-slate-800 dark:text-slate-100 relative bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed pr-8"
                :disabled="disabled"
                :placeholder="''"
                v-model="cardQuery"
                @click="toggleDropdown"
                @keydown.enter.prevent="enter"
                @keydown.down="down"
                @keydown.up="up"
                @keydown.esc.prevent="closeDropdown"
                @focus="isFocused = true"
                @blur="isFocused = false"
                ref="cardDropdownButton"
                autocomplete="off"
            />
            <Icon
                icon="chevron-down"
                class="block leading-none text-slate-500 dark:text-slate-400 w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                :class="{ 'rotate-180 transition-transform': showCardsList }"
            />
            <span
                class="absolute inset-y-0 left-4 right-8 flex items-center gap-2 text-slate-800 dark:text-slate-100 text-sm whitespace-nowrap pointer-events-none select-none z-[60] transition-opacity"
                :class="{ 'opacity-0': showCardsList || isFocused || (cardQuery && cardQuery.length) }"
            >
                <template v-if="hasSelectedLabel">
                    <div class="shrink-0">
                        <ImageLoader class="block rounded-sm">
                            <template #image>
                                <img
                                    :src="`${cdnUrl}/${selectedAccount?.card?.card_image?.path}/_small/${selectedAccount?.card?.card_image?.file_name}`"
                                    class="h-5 w-8 rounded-sm object-contain"
                                    loading="eager"
                                />
                            </template>
                            <template #preloader>
                                <div
                                    class="h-5 w-[31.72px] rounded-sm bg-slate-100 dark:bg-slate-700"
                                ></div>
                            </template>
                            <template #error>
                                <Icon icon="credit-card" class="w-8 h-5" />
                            </template>
                        </ImageLoader>
                    </div>
                    <div class="flex-1 min-w-0 flex items-center gap-1">
                        <span class="truncate">
                            {{ selectedAccount?.card?.title }}
                        </span>
                        <span
                            class="flex shrink-0"
                            v-if="selectedAccount?.last_four"
                        >
                            ({{ selectedAccount?.last_four }})
                        </span>
                    </div>
                </template>
                <template v-else>
                    <div class="shrink-0 w-8 h-5 rounded-[2px] bg-slate-200 dark:bg-slate-700"></div>
                    <span class="text-slate-500 dark:text-slate-400">Select card</span>
                </template>
            </span>
        </label>
        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 absolute z-[99999]"
                    v-show="showCardsList"
                    ref="dropdownContainer"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <template
                        v-for="(walletUser, i) in filteredWalletUsers"
                        :key="walletUser.id"
                    >
                        <p
                            :class="{
                                'mt-6': i === filteredWalletUsers.length - 1,
                            }"
                            class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm font-medium p-2"
                        >
                            <Icon icon="user" class="w-5 h-5" />
                            {{ walletUser.name }}
                        </p>
                        <div
                            v-for="(institution, i) in walletUser.institutions"
                            :key="institution.id"
                            :class="{ 'mt-2': i > 0 }"
                        >
                            <p
                                class="text-slate-800 dark:text-slate-100 font-medium leading-tight text-sm p-2"
                            >
                                {{ institution.issuing_bank.name }}
                            </p>
                            <div>
                                <button
                                    v-for="account in institution.accounts"
                                    :key="account.id"
                                    class="w-full flex items-center gap-2 p-2 text-slate-800 dark:text-slate-100 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                    :class="{
                                        'bg-slate-100 dark:bg-slate-700':
                                            activeIndex >= 0 &&
                                            flattenedCards[activeIndex]?.id ==
                                                account.id,
                                        'font-medium':
                                            selectedAccount?.id === account.id,
                                    }"
                                    :title="`${account.card?.title} (${account.last_four})`"
                                    @click="selectCard(account, walletUser)"
                                    id="hs-wallet-select-card"
                                >
                                    <div class="shrink-0">
                                        <ImageLoader>
                                            <template #image>
                                                <img
                                                    :src="`${cdnUrl}/${account.card?.card_image?.path}/_small/${account.card?.card_image?.file_name}`"
                                                    :alt="account?.card?.title"
                                                    class="h-5 w-[31.72px] rounded-sm object-contain"
                                                    loading="lazy"
                                                />
                                            </template>
                                            <template #preloader>
                                                <div
                                                    class="h-5 w-[31.72px] rounded-sm bg-slate-100 dark:bg-slate-700"
                                                ></div>
                                            </template>
                                            <template #error>
                                                <Icon
                                                    icon="credit-card"
                                                    class="w-8 h-5"
                                                />
                                            </template>
                                        </ImageLoader>
                                    </div>
                                    <div
                                        class="flex-1 min-w-0 flex items-center gap-1"
                                    >
                                        <span class="truncate">
                                            {{
                                                account.card?.title ||
                                                'Unknown card'
                                            }}
                                        </span>
                                        <span>
                                            {{
                                                account?.last_four
                                                    ? `(${account?.last_four})`
                                                    : ''
                                            }}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </template>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
    nextTick,
} from 'vue';
import { useWalletStore } from '@/store/wallet';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        default: null,
    },
    excludeCard: {
        type: Object,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const walletStore = useWalletStore();
const { wallet } = storeToRefs(walletStore);

const selectedAccount = computed(() => {
    if (!props.modelValue) return null;
    return props.modelValue.account ?? props.modelValue;
});

const hasSelectedLabel = computed(() => {
    const acc = selectedAccount.value;
    return !!acc && (!!acc.card?.title || !!acc.last_four);
});

const cardQuery = ref('');
const activeIndex = ref(-1);
const showCardsList = ref(false);
const cardDropdownButton = useTemplateRef('cardDropdownButton');
const dropdownContainer = useTemplateRef('dropdownContainer');
const top = ref(0);
const left = ref(0);
const width = ref(80);

// Debounce the search to prevent excessive re-renders
const debouncedCardQuery = computed(() => {
    return cardQuery.value;
});

const cdnUrl = computed(() => window.cdnUrl);

const filteredWalletUsers = computed(() => {
    if (!debouncedCardQuery.value) {
        // Apply exclude filter even when there's no search
        return filterWalletUsers(wallet.value.entities);
    }

    const query = debouncedCardQuery.value.toLowerCase();
    return filterWalletUsers(wallet.value.entities, query);
});

const flattenedCards = computed(() => {
    return walletStore.visibleCards;
});

const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!showCardsList.value) {
        openDropdown();
    } else {
        setTimeout(() => {
            showCardsList.value = false;
        }, 100);
    }
};

const closeDropdown = () => {
    showCardsList.value = false;
    activeIndex.value = -1;
};

const openDropdown = () => {
    showCardsList.value = true;
    activeIndex.value = 0;
};

const enter = () => {
    const selected = flattenedCards.value[activeIndex.value];
    if (selected) {
        // Pass the selected card as the account, and find the user from wallet store if needed
        // We may need the user's ID from the selected card's user_id property
        selectCard(selected, { id: selected.user_id });
    }
};

const up = () => {
    if (showCardsList.value) {
        activeIndex.value = Math.max(activeIndex.value - 1, -1);
        scrollActiveItemIntoView();
    }
};

const down = () => {
    if (!showCardsList.value) {
        showCardsList.value = true;
        activeIndex.value = 0;
    } else {
        activeIndex.value = Math.min(
            activeIndex.value + 1,
            flattenedCards.value.length - 1
        );
    }

    scrollActiveItemIntoView();
};

const scrollActiveItemIntoView = () => {
    if (activeIndex.value >= 0) {
        nextTick(() => {
            const container = dropdownContainer.value;
            const activeItem = container?.querySelector('.bg-slate-100 dark:bg-slate-700');
            if (container && activeItem) {
                const containerRect = container.getBoundingClientRect();
                const itemRect = activeItem.getBoundingClientRect();

                if (itemRect.bottom > containerRect.bottom) {
                    activeItem.scrollIntoView({
                        block: 'end',
                        behavior: 'smooth',
                    });
                } else if (itemRect.top < containerRect.top) {
                    activeItem.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                    });
                }
            }
        });
    }
};

const filterWalletUsers = (users, query = '') => {
    const results = [];

    for (const user of users) {
        const filteredInstitutions = [];

        for (const institution of user.institutions) {
            const filteredAccounts = institution.accounts.filter((account) => {
                // First check if this account should be excluded
                if (account.id === props.excludeCard?.id) {
                    return false;
                }

                // If there's a search query, apply the search filters
                if (query) {
                    return (
                        account.card?.title?.toLowerCase().includes(query) ||
                        account.last_four?.includes(query) ||
                        institution.issuing_bank.name
                            ?.toLowerCase()
                            .includes(query)
                    );
                }

                return true;
            });

            if (filteredAccounts.length > 0) {
                filteredInstitutions.push({
                    ...institution,
                    accounts: filteredAccounts,
                });
            }
        }

        if (filteredInstitutions.length > 0) {
            results.push({
                ...user,
                institutions: filteredInstitutions,
            });
        }
    }

    return results;
};

const setDropdownPosition = () => {
    const position = cardDropdownButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

watch(showCardsList, (val) => {
    if (val) {
        setDropdownPosition();
    }
});

const selectCard = (account, user) => {
    emit('update:modelValue', { account, user });
    cardQuery.value = '';
    showCardsList.value = false;
};

onMounted(() => {
    window.addEventListener('resize', setDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.addEventListener('scroll', setDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.removeEventListener('scroll', setDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-from {
    opacity: 0;
}
</style>
